import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';

import './index.less';

import IbButton from '../../../../components/common/IbButton';
import IbModal, { IbModalPosition } from '../../../../components/common/IbModal';
import { ProjectCreationRequest, ProjectModel, ProjectUpdatingRequest } from '../../../../../gamma-api';
import IbTypography from '../../../../components/common/IbTypography';
import IbInput from '../../../../components/common/IbInput';
import { ValidationErrors } from '../../../../types';
import { useValidationForm, CheckFieldValueFnMap } from '../../../../utils/validationUtil';
import { currentFolderIdSelector } from '../../../../recoil';

import { FORM_ITEM_CLASS_NAME, MAIN_CLASS_NAME, EMPTY_PROJECT } from './const';

type ProjectForm = {
  name: string;
};

const checkFieldValueFnMap: CheckFieldValueFnMap<ProjectForm> = {
  name: (value: string) => !!value?.length,
};

interface IProjectModalProps {
  visible: boolean;
  editProject?: ProjectModel;
  onAdd?: (project: ProjectCreationRequest, onSubmitErrors: (errors: ValidationErrors) => void) => Promise<void>;
  onEdit?: (project: ProjectUpdatingRequest, onSubmitErrors: (errors: ValidationErrors) => void) => Promise<void>;
  onCancel?: () => void;
}

const ProjectModal: React.FC<IProjectModalProps> = ({ visible, editProject, onAdd, onEdit, onCancel }) => {
  const { t } = useTranslation();

  const folderId = useRecoilValue(currentFolderIdSelector);

  const [saving, setSaving] = useState(false);
  const [project, setProject] = useState<ProjectModel>(EMPTY_PROJECT);

  const initialFormValues: ProjectForm = {
    name: editProject?.name ?? '',
  };

  const {
    validationState,
    onFieldChange,
    onFieldBlur,
    onSubmitErrors,
    validate,
    reset,
    getShowError,
    getErrorMessage,
  } = useValidationForm<ProjectForm>(initialFormValues, checkFieldValueFnMap);

  const onVisiblePropChange = () => {
    if (!visible) return;

    setSaving(false);
    setProject(editProject ?? EMPTY_PROJECT);
    reset();
  };
  useEffect(onVisiblePropChange, [visible]);

  const onNameChange = (value: string) => {
    setProject({ ...project, name: value });
    onFieldChange('name', value);
  };

  const onNameBlur = () => onFieldBlur('name');

  const onCreateButtonClick = async () => {
    if (!onAdd || !validate()) return;

    setSaving(true);
    await onAdd({ folder_id: folderId, name: project.name }, onSubmitErrors);
    setSaving(false);
  };

  const onSaveButtonClick = async () => {
    if (!onEdit || !editProject || !validate()) return;

    setSaving(true);
    await onEdit(
      {
        name: project.name,
        pipeline: project.pipeline,
        widget: project.widget,
        agent_tools: project.agent_tools,
      },
      onSubmitErrors
    );
    setSaving(false);
  };

  const renderModalContent = () => {
    return (
      <>
        <div className={FORM_ITEM_CLASS_NAME}>
          <IbTypography.Paragraph type="secondary">{t('Project modal: Name: Title')}</IbTypography.Paragraph>
          <IbInput
            status={getShowError(validationState.name) ? 'error' : 'default'}
            value={project.name}
            onBlur={onNameBlur}
            onChange={onNameChange}
          />
          {getShowError(validationState.name) && (
            <IbTypography.Paragraph error type="descriptor">
              {getErrorMessage(validationState.name, t('Project modal: Name: Error'))}
            </IbTypography.Paragraph>
          )}
        </div>
        {editProject && (
          <div className={FORM_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph type="secondary">{t('External Id')}</IbTypography.Paragraph>
            <IbInput disabled value={project.external_id} />
          </div>
        )}
      </>
    );
  };

  const renderFooter = () => {
    return (
      <>
        {editProject ? (
          <IbButton type="primary" onClick={onSaveButtonClick}>
            {t('Save')}
          </IbButton>
        ) : (
          <IbButton type="primary" onClick={onCreateButtonClick}>
            {t('Create')}
          </IbButton>
        )}
        <IbButton type="secondary" onClick={onCancel}>
          {t('Cancel')}
        </IbButton>
      </>
    );
  };

  const title = editProject ? t('Edit project') : t('Create project');

  return (
    <IbModal
      className={MAIN_CLASS_NAME}
      footer={renderFooter()}
      loading={saving}
      position={IbModalPosition.Centered}
      title={title}
      visible={visible}
      onCancel={onCancel}
    >
      {renderModalContent()}
    </IbModal>
  );
};

export default ProjectModal;
